import {
  FC, memo, useState, useCallback, useEffect,
} from 'react';
import classnames from 'classnames';
import { createUseStyles } from 'react-jss';

import { ICashItem } from 'app/store/nodes/gift/interfaces';

import Item from './elements/Item';

const extraBlocks = (count: number, columns: number): { id: number, name: '', cardImageUrl: '' }[] => {
  if (count % columns === 0) {
    return [];
  }
  const extra = columns - (count % columns);
  return new Array(extra).fill(null).map((_, index) => ({
    id: index,
    name: '',
    cardImageUrl: '',
  }));
};

interface GridProps {
  className?: string,
  items: ICashItem[],
  onChoose?: (id: number) => void,
}

const Grid: FC<GridProps> = (props) => {
  const {
    className,
    items,
    onChoose,
  } = props;

  const classes = useStyles();

  const [columns, setColumns] = useState(1);

  const handleChange = useCallback((id: number) => {
    onChoose?.(id);
  }, [onChoose]);

  const handleResize = useCallback(() => {
    let value = 2;
    if (window.innerWidth >= 768) {
      value = 3;
    }
    if (window.innerWidth >= 1024) {
      value = 4;
    }
    if (value !== columns) {
      setColumns(value);
    }
  }, [columns]);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [handleResize]);

  const itemsFinal = [...items, ...extraBlocks(items.length, columns)];

  return (
    <div className={classnames(className, classes.container)}>
      <div className={classes.grid}>
        {itemsFinal.map((item) => {
          if (!item.name && !item.cardImageUrl) {
            return (
              <div key={`item-extra-${item.id}`} className={classes.itemExtra} />
            );
          }
          return (
            <Item
              key={`item-${item.id}`}
              id={item.id}
              name={item.name}
              thumbnail={item.cardImageUrl}
              onChange={handleChange}
            />
          );
        })}
      </div>
    </div>
  );
};

const useStyles = createUseStyles({
  container: {
    position: 'relative',
    flex: 1,
  },
  grid: {
    '&': {
      display: 'flex',
      position: 'absolute',
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      alignContent: 'start',
      overflow: 'auto',
      flexWrap: 'wrap',
      justifyContent: 'space-between',
    },
    '@media (min-width: 768px)': {
    },
  },
  itemExtra: {
    '&': {
      display: 'flex',
      cursor: 'pointer',
      marginBottom: '1rem',
      width: 'calc(50% - 0.6rem)',
    },
    '@media (min-width: 768px)': {
      width: 'calc(33.3% - 0.6rem)',
    },
    '@media (min-width: 1024px)': {
      width: 'calc(25% - 0.6rem)',
    },
  },
});

export default memo(Grid);
